import React, { useState } from "react";
import {
    DescriptionList,
    DescriptionListItem,
} from "@design-system/DescriptionList/DescriptionList";
import { BooleanControl, PlaygroundSelect } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./DataDisplayPlayground.module.scss";

type DescriptionListLayout = "row" | "stack" | "columns";

const layoutOptions = [
    "row",
    "stack",
    "columns",
] satisfies readonly DescriptionListLayout[];

const compactRecords = [
    { label: "Account", value: "Operating checking ••4821" },
    { label: "Posted", value: "August 12, 2026" },
    { label: "Amount", value: "$2,480.00" },
];

const additionalRecords = [
    { label: "Category", value: "Software & subscriptions" },
    { label: "Entity", value: "Kick Labs, Inc." },
    { label: "Source", value: "Mercury" },
];

export const DescriptionListPlayground = () => {
    const [layout, setLayout] = useState<DescriptionListLayout>("row");
    const [hasLongValue, setHasLongValue] = useState(false);
    const [hasEmptyValue, setHasEmptyValue] = useState(true);
    const [hasManyRows, setHasManyRows] = useState(false);

    const records = hasManyRows
        ? [...compactRecords, ...additionalRecords]
        : compactRecords;

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Layout</h2>
                        <PlaygroundSelect<DescriptionListLayout>
                            options={layoutOptions}
                            value={layout}
                            onChange={setLayout}
                        />
                    </section>
                    <section>
                        <h2>Record states</h2>
                        <div className={styles.booleanGrid}>
                            <BooleanControl
                                label="Long counterparty"
                                checked={hasLongValue}
                                onChange={setHasLongValue}
                            />
                            <BooleanControl
                                label="Empty reference"
                                checked={hasEmptyValue}
                                onChange={setHasEmptyValue}
                            />
                            <BooleanControl
                                label="Many rows"
                                checked={hasManyRows}
                                onChange={setHasManyRows}
                            />
                        </div>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary={`${layout} · ${records.length + 2} rows`}
                output={JSON.stringify(
                    {
                        props: {
                            layout,
                            alignTop: hasLongValue,
                        },
                        showcase: {
                            hasEmptyValue,
                            hasManyRows,
                        },
                    },
                    null,
                    2,
                )}
            >
                <div className={styles.surface}>
                    <DescriptionList>
                        <DescriptionListItem
                            label="Counterparty"
                            layout={layout}
                            alignTop={hasLongValue}
                        >
                            <span className={styles.recordValue}>
                                {hasLongValue
                                    ? "North American Business Operations and Professional Services Holdings, LLC"
                                    : "Acme Cloud Services"}
                            </span>
                        </DescriptionListItem>
                        {records.map((record) => (
                            <DescriptionListItem
                                key={record.label}
                                label={record.label}
                                layout={layout}
                            >
                                <span className={styles.recordValue}>
                                    {record.value}
                                </span>
                            </DescriptionListItem>
                        ))}
                        <DescriptionListItem label="Reference" layout={layout}>
                            <span className={styles.recordValue}>
                                {hasEmptyValue ? "" : "INV-2026-0842"}
                            </span>
                        </DescriptionListItem>
                    </DescriptionList>
                </div>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
